import React from 'react';
import { SearchBar } from '../../../shared/components/search_bar';
import { Table } from '../../../shared/components/table';
import { Container, ContainerApoio, Header, HeaderImpressao, Title } from './styles';
import { GradePageQuery_oferta, GradePageQuery_turma } from '../../graphql/queries/__generated__/GradePageQuery';
import { PeriodosQuery_periodos } from '../../graphql/queries/__generated__/PeriodosQuery';
import { sigla } from '../../../shared/utils/formatters/sigla.formatter';

/** Formata data ISO para dd/MM/yyyy sem deslocamento de timezone */
const formatarDataUTC = (value: string | null | undefined): string => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

interface IGradeDisciplinas {
  ind_semestre: number;
  id_disciplina: number;
  dsc_nome_disciplina: string;
  dsc_sigla: string;
  num_carga_horaria: number;
  num_carga_horaria_to?: number | null;
  dat_inicio?: string;
  dat_fim?: string;
  progesso_carga_horaria: string;
  dsc_nome_professor: string;
}

interface Props {
  disciplinas: IGradeDisciplinas[];
  oferta: GradePageQuery_oferta;
  turma: GradePageQuery_turma;
  periodo?: PeriodosQuery_periodos;
  num_grade?: string | null;
  num_semestre?: number | null;
  num_ano?: number | null;
}

const GradeDisciplinas: React.FC<Props> = (props) => {
  const [searchTerm, setSearchTerm] = React.useState<string>('');

  const filtrar_disciplinas = () => {
    return props.disciplinas
      ? props.disciplinas.filter((d) =>
          d.dsc_nome_disciplina?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : [];
  };

  const formatar_periodo = (periodo: PeriodosQuery_periodos | undefined) => {
    if (!periodo) return '-';

    const semestre = periodo.dsc_periodo.split('-')[1] === '1' ? '1º' : '2º';
    const inicio = formatarDataUTC(periodo.dat_inicio);
    const fim = formatarDataUTC(periodo.dat_fim);

    return `(${semestre} semestre): ${inicio} a ${fim}`;
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'Disciplinas',
        accessor: 'dsc_nome_disciplina',
        sortType: 'basic',
        style: { width: '38%' },
      },
      {
        Header: 'Cód.',
        accessor: 'dsc_sigla',
        sortType: 'basic',
        style: { width: '6%', textAlign: 'center' },
      },
      {
        Header: 'Ch (Ao Vivo)',
        accessor: 'num_carga_horaria',
        sortType: 'basic',
        style: { width: '6%', textAlign: 'center' },
      },
      {
        Header: 'Ch (TO)',
        accessor: 'num_carga_horaria_to',
        sortType: 'basic',
        style: { width: '6%', textAlign: 'center' },
      },
      {
        Header: 'Professor(a)',
        accessor: 'dsc_nome_professor',
        sortType: 'basic',
        style: { width: '24%' },
      },
      {
        Header: 'Início',
        accessor: 'dat_inicio',
        sortType: 'basic',
        style: { width: '10%', textAlign: 'center' },
        Cell: ({ value }: { value: string }) => <>{formatarDataUTC(value)}</>,
      },
      {
        Header: 'Término',
        accessor: 'dat_fim',
        sortType: 'basic',
        style: { width: '10%', textAlign: 'center' },
        Cell: ({ value }: { value: string }) => <>{formatarDataUTC(value)}</>,
      },
      {
        Header: 'Ocupação',
        accessor: 'progesso_carga_horaria',
        sortType: 'basic',
      },
    ],
    []
  );

  return (
    <>
      <HeaderImpressao>
        <thead>
          <tr>
            <th colSpan={2}>
              Diretoria de Educação Continuada
              <br />
              || GRADE HORÁRIA || {props.num_semestre}º semestre de {props.num_ano} ||
            </th>
            <th>
              <img
                src={`${process.env.PUBLIC_URL}/images/logo-pucminas-grade.png`}
                style={{ objectFit: 'contain' }}
                width={'100'}
                height={'100'}
                alt='Logo'
              />
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td colSpan={2}>Curso: {props.oferta.curso?.dsc_nome_curso}</td>
            <td>
              Modalidade: {props.oferta.curso?.unidade?.dsc_nome_unidade}
            </td>
          </tr>
          <tr>
            <td style={{ whiteSpace: 'nowrap', width: '38%' }}>
              Oferta: {props.num_grade} ({props.oferta.num_semestre}º. Semestre de {props.oferta.num_ano})
            </td>
            <td style={{ width: '18%' }}>
              Turma: {sigla(props.oferta.curso?.dsc_nome_curso, props.oferta.curso?.dsc_sigla_curso)} {props.oferta.num_oferta}.{props.turma.num_turma}
            </td>
            <td style={{ width: '44%' }}>
              Período do curso: {props.oferta.dat_inicio && props.oferta.dat_fim
                ? `${formatarDataUTC(props.oferta.dat_inicio)} a ${formatarDataUTC(props.oferta.dat_fim)}`
                : '-'}
            </td>
          </tr>
        </tbody>
      </HeaderImpressao>

      <Container>
        <Header>
          <Title>
            Curso: {props.oferta.curso?.dsc_nome_curso} - {props.num_grade}
          </Title>
          <SearchBar onSearch={setSearchTerm} placeholder='Filtrar disciplinas...' width='320px' />
        </Header>

        <Table
          data={filtrar_disciplinas()}
          columns={columns}
          getCellProps={(cellInfo: any) => {
            if (cellInfo.column.id === 'progesso_carga_horaria') {
              return {
                className: 'progesso_carga_horaria',
                style: { '--progesso_carga_horaria': cellInfo.value } as React.CSSProperties,
              };
            } else {
              return {};
            }
          }}
          getRowProps={(rowInfo: any) => {
            const original = rowInfo.original as IGradeDisciplinas;
            return {
              className: 'semestre_' + original.ind_semestre,
            };
          }}
        />
      </Container>

      <ContainerApoio>
        <p><strong>Contatos:</strong></p>
        <p>Secretaria: apoio: (31) 3131-2800 – <a href={`mailto:${props.oferta.curso?.dsc_email_apoio}`}>{props.oferta.curso?.dsc_email_apoio}</a> | Assistente: {props.oferta.assistente_academico ? <>{props.oferta.assistente_academico.dsc_nome_assitente} – <a href={`mailto:${props.oferta.assistente_academico.dsc_email}`}>{props.oferta.assistente_academico.dsc_email}</a></> : <span>Não cadastrado</span>}</p>
        <p>Financeiro: abertura de chamado pelo SGA Aluno. O acesso pode ser realizado pelo aplicativo PUC Minas Mobile, ou no seu navegador utilizando o link: (<a href="https://www.sistemas.pucminas.br/sgaaluno4/SilverStream/Pages/pgAln_LoginSSL.html">https://www.sistemas.pucminas.br/sgaaluno4/SilverStream/Pages/pgAln_LoginSSL.html</a>).</p>
        <p>Informática: TI: (31) 3131-2800 (ou pelo link: <a href="https://forms.office.com/r/DW8SPipYfr?origin=lprLink">https://forms.office.com/r/DW8SPipYfr?origin=lprLink</a>) | Núcleo Virtual:  <a href="mailto:nucleovirtual@pucminas.br">nucleovirtual@pucminas.br</a></p>
        <br/>
        <p><strong>Período letivo</strong> (de acordo com o calendário do CRA): <strong>{formatar_periodo(props.periodo)}</strong></p>
      </ContainerApoio>
    </>
  );
};

export { GradeDisciplinas };
export type { IGradeDisciplinas };
